import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { formatCurrency, getFinancialYearString } from './gstCalculations';

/**
 * Export Service for generating Excel/CSV reports
 * Handles quarterly GST reports, purchase/sales data export
 */

class ExportService {
  
  /**
   * Export quarterly GST summary to Excel
   */
  async exportQuarterlySummary(summary, quarter, year, businessName = 'Business') {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      ['GST QUARTERLY RETURN SUMMARY'],
      [`Financial Year: ${getFinancialYearString(new Date(year, 3, 1))}`],
      [`Quarter: ${quarter}`],
      [`Business Name: ${businessName}`],
      [''],
      ['PURCHASES SUMMARY'],
      ['Total Purchases', formatCurrency(summary.purchases.totalAmount)],
      ['Base Amount (Excluding GST)', formatCurrency(summary.purchases.baseAmount)],
      ['Total ITC Available', formatCurrency(summary.purchases.totalITC)],
      ['Number of Purchase Invoices', summary.purchases.count],
      [''],
      ['SALES SUMMARY'],
      ['Total Sales', formatCurrency(summary.sales.totalAmount)],
      ['Base Amount (Excluding GST)', formatCurrency(summary.sales.baseAmount)],
      ['Total Output GST', formatCurrency(summary.sales.totalOutputGST)],
      ['Number of Sales Invoices', summary.sales.count],
      [''],
      ['GST CALCULATION'],
      ['Output GST (Sales)', formatCurrency(summary.sales.totalOutputGST)],
      ['Less: ITC (Purchases)', formatCurrency(summary.purchases.totalITC)],
      [''],
      ['NET GST PAYABLE', formatCurrency(summary.netGSTPayable)],
      summary.netGSTRefundable > 0 ? ['NET GST REFUNDABLE', formatCurrency(summary.netGSTRefundable)] : []
    ].filter(row => row.length > 0);

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Style the summary sheet
    this.styleWorksheet(summarySheet);

    const fileName = `GST_Summary_${quarter}_${getFinancialYearString(new Date(year, 3, 1))}.xlsx`;
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, fileName);
  }

  /**
   * Export purchases data to Excel
   */
  async exportPurchases(purchases, quarter = null, year = null) {
    const workbook = XLSX.utils.book_new();
    
    const headers = [
      'Date',
      'Supplier Name',
      'Invoice Number',
      'Base Amount',
      'GST Amount',
      'Total Amount',
      'ITC Available',
      'Payment Mode',
      'Quarter',
      'Year'
    ];

    const data = purchases.map(purchase => [
      purchase.purchaseDate ? new Date(purchase.purchaseDate).toLocaleDateString('en-IN') : '',
      purchase.supplierName || '',
      purchase.invoiceNumber || '',
      purchase.baseAmount || 0,
      purchase.gstAmount || 0,
      purchase.totalAmount || 0,
      purchase.itcAmount || 0,
      purchase.paymentMode || '',
      purchase.quarter || '',
      purchase.year || ''
    ]);

    const worksheetData = [headers, ...data];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths
    worksheet['!cols'] = [
      { width: 12 }, // Date
      { width: 20 }, // Supplier Name
      { width: 15 }, // Invoice Number
      { width: 12 }, // Base Amount
      { width: 12 }, // GST Amount
      { width: 12 }, // Total Amount
      { width: 12 }, // ITC Available
      { width: 15 }, // Payment Mode
      { width: 8 },  // Quarter
      { width: 8 }   // Year
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchases');

    const quarterStr = quarter && year ? `_${quarter}_${year}` : '';
    const fileName = `Purchases${quarterStr}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, fileName);
  }

  /**
   * Export sales data to Excel
   */
  async exportSales(sales, quarter = null, year = null) {
    const workbook = XLSX.utils.book_new();
    
    const headers = [
      'Date',
      'Customer Name',
      'Invoice Number',
      'Base Amount',
      'CGST',
      'SGST',
      'Total GST',
      'Total Amount',
      'Payment Method',
      'Quarter',
      'Year'
    ];

    const data = sales.map(sale => [
      sale.saleDate ? new Date(sale.saleDate).toLocaleDateString('en-IN') : '',
      sale.customerName || 'Cash Customer',
      sale.invoiceNumber || '',
      sale.baseAmount || 0,
      sale.cgst || 0,
      sale.sgst || 0,
      sale.gstAmount || 0,
      sale.totalAmount || 0,
      sale.paymentMethod || '',
      sale.quarter || '',
      sale.year || ''
    ]);

    const worksheetData = [headers, ...data];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths
    worksheet['!cols'] = [
      { width: 12 }, // Date
      { width: 20 }, // Customer Name
      { width: 15 }, // Invoice Number
      { width: 12 }, // Base Amount
      { width: 10 }, // CGST
      { width: 10 }, // SGST
      { width: 12 }, // Total GST
      { width: 12 }, // Total Amount
      { width: 15 }, // Payment Method
      { width: 8 },  // Quarter
      { width: 8 }   // Year
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales');

    const quarterStr = quarter && year ? `_${quarter}_${year}` : '';
    const fileName = `Sales${quarterStr}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, fileName);
  }

  /**
   * Export complete quarterly report with all data
   */
  async exportCompleteQuarterlyReport(summary, purchases, sales, quarter, year, businessInfo = {}) {
    const workbook = XLSX.utils.book_new();
    
    // 1. Summary Sheet
    const summaryData = [
      ['GST QUARTERLY RETURN - COMPLETE REPORT'],
      [`Financial Year: ${getFinancialYearString(new Date(year, 3, 1))}`],
      [`Quarter: ${quarter}`],
      [`Business Name: ${businessInfo.businessName || 'Business'}`],
      [`GST Number: ${businessInfo.gstNumber || 'Not Provided'}`],
      [`Report Generated: ${new Date().toLocaleString('en-IN')}`],
      [''],
      ['QUARTERLY SUMMARY'],
      ['', 'Count', 'Base Amount', 'GST Amount', 'Total Amount'],
      ['Purchases', summary.purchases.count, summary.purchases.baseAmount, summary.purchases.totalITC, summary.purchases.totalAmount],
      ['Sales', summary.sales.count, summary.sales.baseAmount, summary.sales.totalOutputGST, summary.sales.totalAmount],
      [''],
      ['GST CALCULATION'],
      ['Output GST (Sales)', formatCurrency(summary.sales.totalOutputGST)],
      ['Less: ITC (Purchases)', formatCurrency(summary.purchases.totalITC)],
      ['NET GST PAYABLE', formatCurrency(summary.netGSTPayable)],
      summary.netGSTRefundable > 0 ? ['NET GST REFUNDABLE', formatCurrency(summary.netGSTRefundable)] : []
    ].filter(row => row.length > 0);

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // 2. Purchases Sheet
    const purchaseHeaders = [
      'Date', 'Supplier', 'Invoice No', 'Base Amount', 'GST Amount', 
      'Total Amount', 'ITC Available', 'Payment Mode', 'Invoice URL'
    ];
    
    const purchaseData = purchases.map(p => [
      p.purchaseDate ? new Date(p.purchaseDate).toLocaleDateString('en-IN') : '',
      p.supplierName || '',
      p.invoiceNumber || '',
      p.baseAmount || 0,
      p.gstAmount || 0,
      p.totalAmount || 0,
      p.itcAmount || 0,
      p.paymentMode || '',
      p.invoiceUrl || ''
    ]);

    const purchaseSheet = XLSX.utils.aoa_to_sheet([purchaseHeaders, ...purchaseData]);
    XLSX.utils.book_append_sheet(workbook, purchaseSheet, 'Purchases');

    // 3. Sales Sheet
    const salesHeaders = [
      'Date', 'Customer', 'Invoice No', 'Base Amount', 'CGST', 
      'SGST', 'Total GST', 'Total Amount', 'Payment Method', 'Invoice URL'
    ];
    
    const salesData = sales.map(s => [
      s.saleDate ? new Date(s.saleDate).toLocaleDateString('en-IN') : '',
      s.customerName || 'Cash Customer',
      s.invoiceNumber || '',
      s.baseAmount || 0,
      s.cgst || 0,
      s.sgst || 0,
      s.gstAmount || 0,
      s.totalAmount || 0,
      s.paymentMethod || '',
      s.invoiceUrl || ''
    ]);

    const salesSheet = XLSX.utils.aoa_to_sheet([salesHeaders, ...salesData]);
    XLSX.utils.book_append_sheet(workbook, salesSheet, 'Sales');

    const fileName = `Complete_GST_Report_${quarter}_${getFinancialYearString(new Date(year, 3, 1))}.xlsx`;
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, fileName);
  }

  /**
   * Export data to CSV format
   */
  async exportToCSV(data, filename, headers) {
    const csvData = [headers, ...data];
    const worksheet = XLSX.utils.aoa_to_sheet(csvData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}.csv`);
  }

  /**
   * Apply basic styling to worksheet
   */
  styleWorksheet(worksheet) {
    // This is a basic implementation
    // For advanced styling, you would need xlsx-style or similar
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) continue;
        
        // Basic formatting - this is limited without xlsx-style
        worksheet[cellAddress].z = '#,##0.00'; // Number format for currency cells
      }
    }
  }

  /**
   * Generate GST portal ready format (GSTR-1/GSTR-3B format)
   */
  async exportGSTPortalFormat(summary, purchases, sales, quarter, year, businessInfo = {}) {
    const workbook = XLSX.utils.book_new();
    
    // GSTR-3B Format Summary
    const gstr3bData = [
      ['GSTR-3B SUMMARY'],
      [`GSTIN: ${businessInfo.gstNumber || ''}`],
      [`Period: ${quarter} ${getFinancialYearString(new Date(year, 3, 1))}`],
      [''],
      ['3.1 Details of Outward Supplies and inward supplies liable to reverse charge'],
      ['Nature of Supplies', 'Taxable Value', 'Central Tax', 'State/UT Tax', 'Integrated Tax', 'Cess'],
      ['(a) Outward taxable supplies', summary.sales.baseAmount, summary.sales.totalOutputGST/2, summary.sales.totalOutputGST/2, 0, 0],
      [''],
      ['4.1 Details of ITC Available'],
      ['Details', 'Integrated Tax', 'Central Tax', 'State/UT Tax', 'Cess'],
      ['(A) ITC on inward supplies', 0, summary.purchases.totalITC/2, summary.purchases.totalITC/2, 0],
      [''],
      ['5.1 Interest and late fee for previous tax period'],
      ['Tax', 'Interest', 'Late Fee'],
      ['Central Tax', 0, 0],
      ['State/UT Tax', 0, 0],
      [''],
      ['6.1 Payment of Tax'],
      ['Description', 'Tax Payable', 'ITC', 'Cash', 'Interest', 'Late Fee'],
      ['Central Tax', summary.sales.totalOutputGST/2, summary.purchases.totalITC/2, Math.max(0, (summary.sales.totalOutputGST - summary.purchases.totalITC)/2), 0, 0],
      ['State/UT Tax', summary.sales.totalOutputGST/2, summary.purchases.totalITC/2, Math.max(0, (summary.sales.totalOutputGST - summary.purchases.totalITC)/2), 0, 0]
    ];

    const gstr3bSheet = XLSX.utils.aoa_to_sheet(gstr3bData);
    XLSX.utils.book_append_sheet(workbook, gstr3bSheet, 'GSTR-3B');

    const fileName = `GSTR3B_${quarter}_${getFinancialYearString(new Date(year, 3, 1))}.xlsx`;
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(blob, fileName);
  }
}

// Create singleton instance
const exportService = new ExportService();
export default exportService;
